import cx from 'classnames';
import { memo, useMemo } from 'react';
import { isAndroid, isIOS } from 'react-device-detect';
import shallow from 'zustand/shallow';

import { useIsBreakpointWidth } from 'lib/hooks/useIsBreakpointWidth';

import { Text } from 'components/@tedui';
import Controls from 'components/video-player/InternalPlayer/Ads/Controls';
import useVideoPlayerStore from 'components/video-player/store';

function AdsContainer({
  adContainerRef,
  adLoading,
  adError,
  adTimeRemaining,
  isAdBreak,
  isSkipCountdownActive,
  togglePlay,
  playerTalkData
}): React.ReactNode {
  const isMobileDevice = useMemo(() => isAndroid || isIOS, []);
  const isMobileWeb = useIsBreakpointWidth({
    size: 'xl',
    breakPointType: 'tui'
  });
  const isMobile = isMobileDevice || isMobileWeb;

  const { contentState, buffering, ready, playbackMode, onAdClick } =
    useVideoPlayerStore(
      state => ({
        ...state
      }),
      shallow
    );

  const hideAdInfo =
    !isAdBreak ||
    !ready ||
    buffering ||
    adLoading ||
    adError ||
    playbackMode === 'unstarted' ||
    playbackMode === 'ended' ||
    contentState === 'content';

  return (
    <>
      <div
        id="ad-info"
        className={cx('absolute top-0 h-[calc(100%_-_48px)] w-full', {
          'pointer-events-none': hideAdInfo
        })}
      />
      <div
        id="ad-ui"
        slot="middle-chrome"
        ref={adContainerRef}
        className={cx('min-h-24', {
          hidden: hideAdInfo
        })}
      >
        {isMobile ? (
          <>
            <div className="absolute left-2 top-2 z-50 flex min-h-6 items-center rounded-sm bg-black/25 px-2 py-1">
              <div className="pointer-events-none leading-none">
                <Text
                  variant="caption"
                  tag="p"
                  UNSAFE_className="text-[0.625rem]"
                  color={{
                    isOnLightSurface: false
                  }}
                >
                  TED is supported by ads and partners{' '}
                  <span id="ad-timer" className="text-white">
                    {adTimeRemaining}
                  </span>
                </Text>
              </div>
            </div>

            <div
              className="absolute bottom-16 left-0"
              onClick={onAdClick}
              tabIndex={-1}
              onKeyDown={onAdClick}
              role="button"
            >
              <button
                id="ad-learn-more"
                type="button"
                aria-label="Learn more about this ad"
                className="pointer-events-auto relative min-h-8 min-w-20 whitespace-nowrap rounded-r-sm bg-black/60 px-2 py-[0.375rem] text-white underline underline-offset-2"
              >
                <Text
                  variant="caption"
                  isBold
                  tag="span"
                  color={{
                    isOnLightSurface: false
                  }}
                >
                  Visit advertiser
                </Text>
              </button>
            </div>
            <Controls
              adTimeRemaining={adTimeRemaining}
              playerTalkData={playerTalkData}
            />
          </>
        ) : (
          <>
            <section className="absolute bottom-16 left-8 flex flex-col gap-1 rounded-sm bg-white/50 px-1 py-1">
              <div className="flex items-center justify-between gap-1">
                <div className="pointer-events-none leading-none">
                  <Text
                    variant="caption"
                    tag="p"
                    UNSAFE_className="text-[0.625rem]"
                  >
                    TED is supported by ads and partners{' '}
                    <span id="ad-timer" className="text-black">
                      {adTimeRemaining}
                    </span>
                  </Text>
                </div>
              </div>

              <div
                className={cx(
                  'relative flex min-h-16 min-w-64 items-start justify-between gap-8 rounded-sm bg-white/[31%] p-2',
                  {
                    skippable: isSkipCountdownActive
                  }
                )}
                onClick={onAdClick}
                tabIndex={-1}
                onKeyDown={onAdClick}
                role="button"
              >
                <Text variant="body1" tag="h3">
                  From Our Partners
                </Text>

                <div className="pointer-events-none">
                  <button
                    id="ad-learn-more"
                    aria-label="Learn more about this ad"
                    type="button"
                    className="pointer-events-auto relative whitespace-nowrap rounded-sm bg-black p-2"
                  >
                    <Text
                      variant="caption"
                      isBold
                      tag="span"
                      color={{
                        isOnLightSurface: false
                      }}
                    >
                      Learn more
                    </Text>
                  </button>
                </div>
              </div>
            </section>
            <Controls
              adTimeRemaining={adTimeRemaining}
              playerTalkData={playerTalkData}
            />
          </>
        )}
      </div>
    </>
  );
}

export default memo(AdsContainer);
